/**
 * ChatBubble — floating in-app conversation widget.
 *
 * Mount once in Layout and it appears on every page (bottom-right).
 *
 *  • Collapsed: round 56px button with an unread-count badge.
 *  • Open:      a 360 × 540 panel with three tabs:
 *      - Chats     : conversation list + selected conversation thread + composer
 *      - Tasks     : every task across my conversations, status togglable
 *      - Transfers : recent Material Flow transfers whose destination matches
 *                    one of my departments
 *
 * Polling is the simplest possible — every 12 s when the panel is open we
 * refresh the conversation list (for unread badges) and the active
 * conversation's messages via `?since=...`. No WebSocket.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle, X, Plus, Send, CheckSquare, Square, Loader2,
  Users, MessagesSquare, Truck, Search, ListTodo, CheckCheck,
} from "lucide-react";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
const POLL_MS = 12000;

const STATUS_LABEL = { open: "Open", in_progress: "In Progress", done: "Done" };
const STATUS_NEXT  = { open: "in_progress", in_progress: "done", done: "open" };
const STATUS_COLOR = {
  open:        "bg-amber-100 text-amber-700 border-amber-300",
  in_progress: "bg-blue-100  text-blue-700  border-blue-300",
  done:        "bg-emerald-100 text-emerald-700 border-emerald-300",
};

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "2-digit" }) +
         " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function currentUsername() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    const u = JSON.parse(raw);
    return (u?.username || "").trim();
  } catch { return ""; }
}

export default function ChatBubble() {
  const me = currentUsername();
  const [open, setOpen]           = useState(false);
  const [tab,  setTab]            = useState("chats"); // chats | tasks | transfers
  const [conversations, setConvs] = useState([]);
  const [activeId, setActiveId]   = useState("");
  const [messages, setMessages]   = useState([]);
  const [users,    setUsers]      = useState([]);
  const [transfers,setTransfers]  = useState([]);
  const [newOpen,  setNewOpen]    = useState(false);

  // Skip rendering entirely on the login screen.
  const skipRender = !me;

  // ── Loaders ────────────────────────────────────────────────────
  const loadConvs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/conversations`);
      setConvs(r.data?.conversations || []);
    } catch (e) { /* silent — widget should never spam errors */ }
  }, []);

  const loadMessages = useCallback(async (cid, sinceIso = "") => {
    if (!cid) return;
    try {
      const url = `${API}/chat/conversations/${cid}/messages${sinceIso ? `?since=${encodeURIComponent(sinceIso)}` : ""}`;
      const r = await axios.get(url);
      const incoming = r.data?.messages || [];
      if (sinceIso) {
        setMessages(prev => {
          const ids = new Set(prev.map(m => m.id));
          return [...prev, ...incoming.filter(m => !ids.has(m.id))];
        });
      } else {
        setMessages(incoming);
      }
    } catch (e) { /* silent */ }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/users`);
      setUsers(r.data?.users || []);
    } catch (e) { /* silent */ }
  }, []);

  const loadTransfers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/transfers-to-me?limit=80`);
      setTransfers(r.data?.transfers || []);
    } catch (e) { /* silent */ }
  }, []);

  // ── Open/poll lifecycle ────────────────────────────────────────
  useEffect(() => {
    if (skipRender) return;
    // Always load convs once on mount so the unread badge is correct
    // even when the panel is closed.
    loadConvs();
  }, [skipRender, loadConvs]);

  useEffect(() => {
    if (skipRender || !open) return;
    loadConvs();
    if (tab === "transfers") loadTransfers();
    const t = setInterval(() => {
      loadConvs();
      if (activeId) {
        const lastTs = messagesRef.current?.[messagesRef.current.length - 1]?.created_at || "";
        loadMessages(activeId, lastTs);
      }
      if (tab === "transfers") loadTransfers();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [skipRender, open, tab, activeId, loadConvs, loadMessages, loadTransfers]);

  // keep latest messages in a ref so the polling closure stays simple
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // ── Helpers ────────────────────────────────────────────────────
  const totalUnread = useMemo(
    () => conversations.reduce((s, c) => s + (c.unread || 0), 0),
    [conversations],
  );

  const openConversation = async (cid) => {
    setActiveId(cid);
    setMessages([]);
    await loadMessages(cid);
    try {
      await axios.post(`${API}/chat/conversations/${cid}/read`);
      setConvs(prev => prev.map(c => c.id === cid ? { ...c, unread: 0 } : c));
    } catch (e) { /* silent */ }
  };

  const activeConv = conversations.find(c => c.id === activeId);
  const tasksAcross = useMemo(() => {
    // Bubble up *every* task we currently know about — only the messages
    // for the active conversation are loaded, so tasks tab shows a hint
    // when other conversations exist that haven't been opened yet.
    return messages.filter(m => !!m.task);
  }, [messages]);

  if (skipRender) return null;

  return (
    <>
      {/* ─── Floating launcher button ──────────────────────────────── */}
      {!open && (
        <button
          type="button"
          onClick={() => { setOpen(true); setTab("chats"); }}
          data-testid="chat-bubble-launcher"
          className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full
                     bg-indigo-600 hover:bg-indigo-700 text-white shadow-xl
                     flex items-center justify-center transition-colors"
          aria-label="Open chat"
        >
          <MessageCircle className="h-7 w-7" />
          {totalUnread > 0 && (
            <span
              data-testid="chat-bubble-unread"
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1
                         rounded-full bg-rose-500 text-white text-[11px]
                         font-bold flex items-center justify-center">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      )}

      {/* ─── Open panel ────────────────────────────────────────────── */}
      {open && (
        <div
          data-testid="chat-bubble-panel"
          className="fixed bottom-5 right-5 z-[60] w-[360px] max-w-[95vw]
                     h-[540px] max-h-[80vh] rounded-xl bg-white shadow-2xl
                     border border-zinc-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2
                          bg-indigo-600 text-white">
            <div className="font-semibold text-sm flex items-center gap-2">
              <MessagesSquare className="h-4 w-4" />
              Conversations
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-bubble-close"
              className="p-1 rounded hover:bg-indigo-700">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Tabs */}
          <div className="grid grid-cols-3 text-xs font-medium border-b">
            <TabBtn label="Chats"     icon={MessageCircle} active={tab === "chats"}     onClick={() => setTab("chats")}     testid="chat-tab-chats" />
            <TabBtn label="Tasks"     icon={ListTodo}      active={tab === "tasks"}     onClick={() => setTab("tasks")}     testid="chat-tab-tasks" />
            <TabBtn label="Transfers" icon={Truck}         active={tab === "transfers"} onClick={() => { setTab("transfers"); loadTransfers(); }} testid="chat-tab-transfers" />
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {tab === "chats"     && (
              activeId
                ? <ConversationView
                    conv={activeConv} messages={messages} me={me}
                    onBack={() => { setActiveId(""); setMessages([]); }}
                    onSend={async (body, task) => {
                      try {
                        await axios.post(`${API}/chat/conversations/${activeId}/messages`,
                                         { body, task });
                        await loadMessages(activeId);
                        await loadConvs();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Send failed");
                      }
                    }}
                    onConvert={async (msgId, task) => {
                      try {
                        await axios.patch(`${API}/chat/messages/${msgId}/task`, task);
                        await loadMessages(activeId);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Task update failed");
                      }
                    }}
                  />
                : <ConversationList
                    conversations={conversations}
                    me={me}
                    onOpen={openConversation}
                    onNew={() => { setNewOpen(true); loadUsers(); }}
                  />
            )}
            {tab === "tasks"     && (
              <TasksTab
                conversations={conversations}
                activeId={activeId}
                tasksAcross={tasksAcross}
                me={me}
                onPick={openConversation}
                onCycle={async (msgId, currentStatus) => {
                  const next = STATUS_NEXT[currentStatus || "open"] || "open";
                  try {
                    await axios.patch(`${API}/chat/messages/${msgId}/task`, { status: next });
                    if (activeId) await loadMessages(activeId);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Task update failed");
                  }
                }}
              />
            )}
            {tab === "transfers" && <TransfersTab transfers={transfers} />}
          </div>
        </div>
      )}

      {/* New conversation modal */}
      {newOpen && (
        <NewConversationModal
          users={users}
          onClose={() => setNewOpen(false)}
          onCreate={async (payload) => {
            try {
              const r = await axios.post(`${API}/chat/conversations`, payload);
              setNewOpen(false);
              await loadConvs();
              const cid = r.data?.conversation?.id;
              if (cid) openConversation(cid);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Create failed");
            }
          }}
        />
      )}
    </>
  );
}

// ─────────────────────────────── Sub-components ────────────────────────────

function TabBtn({ label, icon: Icon, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`py-2 flex items-center justify-center gap-1 transition-colors
                  ${active ? "bg-white text-indigo-700 border-b-2 border-indigo-600"
                          : "text-zinc-500 hover:bg-zinc-50"}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function ConversationList({ conversations, me, onOpen, onNew }) {
  const [search, setSearch] = useState("");
  const filtered = conversations.filter(c => {
    if (!search) return true;
    const txt = (c.type === "group"
                 ? c.title
                 : (c.participants || []).filter(p => p.toLowerCase() !== me.toLowerCase()).join(", "))
                || "";
    return txt.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <>
      <div className="p-2 flex items-center gap-2 border-b">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search..." data-testid="chat-conv-search"
            className="w-full text-xs pl-7 pr-2 py-1.5 border rounded
                       focus:outline-none focus:ring-1 focus:ring-indigo-500" />
        </div>
        <button
          onClick={onNew}
          data-testid="chat-new-conv-btn"
          className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white
                     px-2 py-1.5 rounded flex items-center gap-1">
          <Plus className="h-3.5 w-3.5" /> New
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-500">
            No conversations yet. Click <b>New</b> to start one.
          </div>
        ) : filtered.map(c => {
          const others = (c.participants || []).filter(p => p.toLowerCase() !== me.toLowerCase());
          const title  = c.type === "group" ? c.title : (others.join(", ") || c.title);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onOpen(c.id)}
              data-testid={`chat-conv-${c.id}`}
              className="w-full text-left px-3 py-2 border-b hover:bg-indigo-50
                         flex items-start gap-2">
              <div className={`mt-0.5 h-8 w-8 rounded-full flex items-center justify-center text-white
                              ${c.type === "group" ? "bg-emerald-500" : "bg-indigo-500"}`}>
                {c.type === "group"
                  ? <Users className="h-4 w-4" />
                  : (title || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-semibold truncate">{title}</div>
                  <div className="text-[10px] text-zinc-400 shrink-0">
                    {fmtTime(c.last_message_at)}
                  </div>
                </div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {c.last_preview || (c.type === "group" ? "Group chat" : "Direct message")}
                </div>
              </div>
              {c.unread > 0 && (
                <span className="bg-rose-500 text-white text-[10px] font-bold
                                 rounded-full min-w-[18px] h-[18px] px-1
                                 flex items-center justify-center self-center">
                  {c.unread}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}

function ConversationView({ conv, messages, me, onBack, onSend, onConvert }) {
  const [text, setText]       = useState("");
  const [sending, setSending] = useState(false);
  const [taskMode, setTaskMode] = useState(false);
  const [dueDate, setDueDate]   = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleSend = async () => {
    if (!text.trim() && !taskMode) return;
    setSending(true);
    try {
      await onSend(
        text.trim(),
        taskMode ? { title: text.trim() || "Task", due_date: dueDate, status: "open" } : null,
      );
      setText(""); setDueDate(""); setTaskMode(false);
    } finally { setSending(false); }
  };

  const others = (conv?.participants || []).filter(p => p.toLowerCase() !== me.toLowerCase());
  const title  = conv?.type === "group" ? conv.title : (others.join(", ") || "Conversation");

  return (
    <>
      <div className="px-3 py-2 border-b flex items-center justify-between gap-2 bg-zinc-50">
        <button onClick={onBack} data-testid="chat-back-btn"
                className="text-xs text-indigo-600 hover:text-indigo-800">
          ← Back
        </button>
        <div className="text-xs font-semibold truncate flex-1 text-center">{title}</div>
        <div className="w-10" />
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-2 space-y-2 bg-zinc-50">
        {messages.length === 0 && (
          <div className="text-center text-[11px] text-zinc-400 py-8">
            No messages yet — say hi 👋
          </div>
        )}
        {messages.map(m => (
          <MessageRow key={m.id} m={m} me={me} onConvert={onConvert} />
        ))}
      </div>
      <div className="border-t p-2 space-y-2 bg-white">
        {taskMode && (
          <div className="flex items-center gap-2 text-xs">
            <CheckSquare className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-amber-700 font-medium">Task mode</span>
            <input
              type="date"
              value={dueDate} onChange={e => setDueDate(e.target.value)}
              className="text-[11px] border rounded px-1 py-0.5"
              data-testid="chat-task-due" />
            <button onClick={() => { setTaskMode(false); setDueDate(""); }}
                    className="ml-auto text-[11px] text-zinc-500 hover:text-zinc-700">
              cancel
            </button>
          </div>
        )}
        <div className="flex items-end gap-1">
          <button
            type="button"
            onClick={() => setTaskMode(t => !t)}
            data-testid="chat-toggle-task"
            className={`p-1.5 rounded border transition-colors
                        ${taskMode ? "bg-amber-50 border-amber-300 text-amber-700"
                                   : "bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50"}`}
            title="Toggle task mode">
            <ListTodo className="h-4 w-4" />
          </button>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={taskMode ? "Task title..." : "Type a message..."}
            rows={1}
            data-testid="chat-input"
            className="flex-1 text-xs px-2 py-1.5 border rounded resize-none
                       focus:outline-none focus:ring-1 focus:ring-indigo-500
                       max-h-24" />
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || (!text.trim() && !taskMode)}
            data-testid="chat-send-btn"
            className="p-1.5 rounded bg-indigo-600 hover:bg-indigo-700
                       disabled:bg-zinc-300 text-white">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </>
  );
}

function MessageRow({ m, me, onConvert }) {
  const mine = m.sender?.toLowerCase() === me.toLowerCase();
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-xs shadow-sm
                       ${mine ? "bg-indigo-600 text-white" : "bg-white border border-zinc-200"}`}>
        {!mine && (
          <div className="text-[10px] font-semibold text-zinc-500 mb-0.5">{m.sender}</div>
        )}
        {m.task ? (
          <div className="space-y-1">
            <div className={`flex items-start gap-1.5 ${mine ? "text-white" : "text-zinc-800"}`}>
              <ListTodo className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div className="font-medium">{m.task.title || m.body}</div>
            </div>
            {m.task.due_date && (
              <div className={`text-[10px] ${mine ? "text-indigo-100" : "text-zinc-500"}`}>
                Due {m.task.due_date}
              </div>
            )}
            <div className="flex items-center gap-1">
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_COLOR[m.task.status || "open"]}`}>
                {STATUS_LABEL[m.task.status || "open"]}
              </span>
              <button
                type="button"
                onClick={() => onConvert(m.id, { status: STATUS_NEXT[m.task.status || "open"] })}
                data-testid={`chat-task-cycle-${m.id}`}
                className={`text-[10px] underline ${mine ? "text-indigo-100 hover:text-white" : "text-indigo-600 hover:text-indigo-800"}`}>
                advance →
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="whitespace-pre-wrap break-words">{m.body}</div>
            <button
              type="button"
              onClick={() => onConvert(m.id, { title: m.body || "Task", status: "open" })}
              data-testid={`chat-msg-to-task-${m.id}`}
              className={`mt-1 text-[10px] underline opacity-70 hover:opacity-100
                          ${mine ? "text-indigo-100" : "text-indigo-600"}`}>
              + convert to task
            </button>
          </>
        )}
        <div className={`text-[9px] mt-0.5 ${mine ? "text-indigo-100" : "text-zinc-400"}`}>
          {fmtTime(m.created_at)}
        </div>
      </div>
    </div>
  );
}

function TasksTab({ conversations, activeId, tasksAcross, me, onPick, onCycle }) {
  const convOptions = conversations;
  if (!activeId) {
    return (
      <div className="p-3 text-xs">
        <div className="text-zinc-600 mb-2">
          Open a conversation to see and manage its tasks here:
        </div>
        <div className="space-y-1 max-h-full overflow-y-auto">
          {convOptions.length === 0 && (
            <div className="text-zinc-400">No conversations yet.</div>
          )}
          {convOptions.map(c => {
            const others = (c.participants || []).filter(p => p.toLowerCase() !== me.toLowerCase());
            const title  = c.type === "group" ? c.title : others.join(", ");
            return (
              <button
                key={c.id}
                onClick={() => onPick(c.id)}
                data-testid={`chat-tasks-pick-${c.id}`}
                className="w-full text-left px-2 py-1.5 border rounded
                           hover:bg-indigo-50 flex items-center justify-between">
                <span className="truncate">{title}</span>
                <CheckCheck className="h-3.5 w-3.5 text-zinc-400" />
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto p-2 space-y-1 text-xs">
      {tasksAcross.length === 0 && (
        <div className="text-center text-zinc-400 py-6">
          No tasks in this conversation yet.
        </div>
      )}
      {tasksAcross.map(m => (
        <div key={m.id} className="border rounded p-2 bg-white">
          <div className="flex items-start gap-2">
            <button
              onClick={() => onCycle(m.id, m.task?.status)}
              data-testid={`chat-tasks-cycle-${m.id}`}
              className="mt-0.5">
              {m.task?.status === "done"
                ? <CheckSquare className="h-4 w-4 text-emerald-600" />
                : <Square className="h-4 w-4 text-zinc-400" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{m.task?.title || m.body}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_COLOR[m.task?.status || "open"]}`}>
                  {STATUS_LABEL[m.task?.status || "open"]}
                </span>
                {m.task?.due_date && (
                  <span className="text-[10px] text-zinc-500">Due {m.task.due_date}</span>
                )}
                <span className="text-[10px] text-zinc-400 ml-auto">
                  by {m.sender} · {fmtTime(m.created_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TransfersTab({ transfers }) {
  return (
    <div className="flex-1 overflow-y-auto p-2 space-y-1 text-xs">
      <div className="text-[11px] text-zinc-500 mb-1">
        Recent transfers landing at your location:
      </div>
      {transfers.length === 0 && (
        <div className="text-center text-zinc-400 py-6">
          Nothing transferred to your location yet.
        </div>
      )}
      {transfers.map(t => {
        const qty = Number(t.transfer_qty || t.total_pcs || t.total_weight || 0);
        const dest = t.to_location || t.transfer_location || "—";
        return (
          <div key={t.id || `${t.sf_code}-${t.created_at}`} className="border rounded p-2 bg-white">
            <div className="flex items-center justify-between gap-2">
              <div className="font-mono font-semibold truncate">
                {t.sf_code || t.article_no || "—"}
              </div>
              <div className="text-[10px] text-zinc-400 shrink-0">
                {fmtTime(t.created_at || t.transfer_date)}
              </div>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap text-[11px] text-zinc-600">
              <span className="font-medium text-indigo-700">{qty}{t.unit ? ` ${t.unit}` : ""}</span>
              <span>·</span>
              <span>{(t.from_location || t.department || "—")} → <b className="text-emerald-700">{dest}</b></span>
              {t.po_number && (<><span>·</span><span className="font-mono">PO {t.po_number}</span></>)}
              {t.transferred_by && (<><span>·</span><span>by {t.transferred_by}</span></>)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NewConversationModal({ users, onClose, onCreate }) {
  const [type, setType]               = useState("dm");
  const [search, setSearch]           = useState("");
  const [picked, setPicked]           = useState(new Set());
  const [title, setTitle]             = useState("");
  const [busy, setBusy]               = useState(false);

  const filtered = users.filter(u =>
    !search || (u.username + " " + (u.full_name || "")).toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (uname) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(uname)) next.delete(uname);
      else if (type === "dm") { next.clear(); next.add(uname); }
      else next.add(uname);
      return next;
    });
  };

  const submit = async () => {
    if (picked.size === 0) return;
    setBusy(true);
    try {
      await onCreate({
        type,
        participants: Array.from(picked),
        title: type === "group" ? (title.trim() || null) : null,
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
         data-testid="chat-new-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm flex flex-col max-h-[80vh]">
        <div className="px-4 py-2 border-b flex items-center justify-between">
          <div className="font-semibold text-sm">New Conversation</div>
          <button onClick={onClose} data-testid="chat-new-close"
                  className="p-1 rounded hover:bg-zinc-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-4 py-2 border-b">
          <div className="flex gap-2 text-xs">
            <button
              onClick={() => { setType("dm"); setPicked(new Set()); }}
              data-testid="chat-new-type-dm"
              className={`px-3 py-1 rounded border
                          ${type === "dm" ? "bg-indigo-600 text-white border-indigo-600"
                                          : "bg-white text-zinc-700 border-zinc-200"}`}>
              Direct Message
            </button>
            <button
              onClick={() => { setType("group"); }}
              data-testid="chat-new-type-group"
              className={`px-3 py-1 rounded border
                          ${type === "group" ? "bg-indigo-600 text-white border-indigo-600"
                                             : "bg-white text-zinc-700 border-zinc-200"}`}>
              Group Chat
            </button>
          </div>
          {type === "group" && (
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Group name (optional)"
              data-testid="chat-new-group-title"
              className="mt-2 w-full text-xs border rounded px-2 py-1.5
                         focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          )}
        </div>
        <div className="px-4 py-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search users..."
              data-testid="chat-new-search"
              className="w-full text-xs pl-7 pr-2 py-1.5 border rounded
                         focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-1">
          {filtered.length === 0 && (
            <div className="text-center text-xs text-zinc-400 py-4">
              No users found.
            </div>
          )}
          {filtered.map(u => {
            const isPicked = picked.has(u.username);
            return (
              <button
                key={u.username}
                onClick={() => toggle(u.username)}
                data-testid={`chat-new-user-${u.username}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded
                            ${isPicked ? "bg-indigo-50" : "hover:bg-zinc-50"}`}>
                {isPicked
                  ? <CheckSquare className="h-4 w-4 text-indigo-600" />
                  : <Square      className="h-4 w-4 text-zinc-300" />}
                <div className="text-left flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{u.full_name || u.username}</div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    @{u.username}
                    {u.departments?.length ? ` · ${u.departments.join(", ")}` : ""}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="chat-new-cancel"
            className="text-xs px-3 py-1.5 rounded border border-zinc-200 hover:bg-zinc-50">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || picked.size === 0}
            data-testid="chat-new-create"
            className="text-xs px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700
                       text-white disabled:bg-zinc-300 flex items-center gap-1">
            {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
